import React, { useState, useEffect } from "react";
import { Card, Form, Button, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import RichTextEditor from "components/common/RichTextEditor";
import paths from "routes/paths";
import useProjects from "hooks/projects/useProjects";
import { useAuth } from "contexts/AuthContext";

const ProjectFormPage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { projects, createProject, updateProject } = useProjects();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [project, setProject] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    description: "",
  });

  const isEditMode = !!projectId;

  useEffect(() => {
    if (isEditMode && projectId && projects.length > 0) {
      const foundProject = projects.find((p) => p.id === projectId);
      if (foundProject) {
        setProject(foundProject);
        setFormData({
          code: foundProject.code || "",
          name: foundProject.name || "",
          description: foundProject.description || "",
        });
        setLoading(false);
      }
    } else if (!isEditMode) {
      setLoading(false);
    }
  }, [projectId, projects, isEditMode]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code.trim()) {
      toast.error("Project code is required");
      return;
    }

    if (!formData.name.trim()) {
      toast.error("Project name is required");
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        // Update existing project
        if (!project?.id) {
          toast.error("Project ID is required");
          return;
        }

        await updateProject(project.id, {
          code: formData.code.trim().toUpperCase(),
          name: formData.name.trim(),
          description: formData.description || null,
        });

        toast.success("Project updated successfully");
        navigate(paths.projectDetails(projectId));
      } else {
        // Create new project
        await createProject({
          code: formData.code.trim().toUpperCase(),
          name: formData.name.trim(),
          description: formData.description || null,
          creatorUserId: user?.id || null,
        });

        toast.success("Project created successfully");
        navigate(paths.projects);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        `Failed to ${isEditMode ? "update" : "create"} project`;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditMode && !project && loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3">
        <WidgetSectionTitle
          icon={isEditMode ? "edit" : "briefcase"}
          title={isEditMode ? `Edit Project: ${project?.name || ""}` : "Create New Project"}
          subtitle={
            isEditMode
              ? "Modify project details and description"
              : "Create a new project to organize iterations and tasks"
          }
          className="mb-3 mt-3 flex-1"
        />

        <Button
          variant="info"
          size="sm"
          className="text-sm"
          onClick={() => navigate(isEditMode ? -1 : paths.projects)}
        >
          Go Back
        </Button>
      </div>

      <Card className="custom-shadow">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-4">
              <Form.Label>Project Code</Form.Label>
              <Form.Control
                type="text"
                value={formData.code}
                onChange={(e) =>
                  handleChange("code", e.target.value.toUpperCase())
                }
                placeholder="e.g., UG, OLITASK, PAYMENTS"
                required
                maxLength={50}
                disabled={isSubmitting}
                autoComplete="off"
              />
              <Form.Text className="text-muted">
                Unique identifier (max 50 characters)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Project Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Enter project name"
                required
                disabled={isSubmitting}
                autoComplete="off"
              />
            </Form.Group>

            <RichTextEditor
              label="Description"
              value={formData.description}
              onChange={(html) => handleChange("description", html)}
            />

            <div className="d-flex justify-content-end mt-7">
              <Button
                variant="success"
                type="submit"
                size="sm"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {isEditMode ? "Saving..." : "Creating..."}
                  </>
                ) : (
                  isEditMode ? "Save Changes" : "Create Project"
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default ProjectFormPage;